import React, { useMemo } from 'react';
import { layout } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { DetailedDesktopTileRender } from 'App/Tile/DetailedDesktopTileRender';
import { ResourceTileRenderer } from 'App/Tile/ResourceTileRenderer';
import { canQueryMachineState } from 'App/Tile/utils';
import { useClientWidth } from 'Components/AppLayout/useClientWidth';
import { centerContentWidthPX } from 'Workspace/dimensions';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

export interface Props {
	resources: Resource[];
}

const MIN_COLUMNS = 4;
const MAX_COLUMNS = 10;
const BASE_WIDTH = 1012;
const WIDTH_PER_COLUMN = 180;

function getColumns(width: number): number {
	const columns = MIN_COLUMNS + 1 + Math.floor((width - BASE_WIDTH) / WIDTH_PER_COLUMN);
	return Math.min(Math.max(columns, MIN_COLUMNS), MAX_COLUMNS);
}

const useGetColumns = () => {
	const width = useClientWidth();
	return useMemo(() => getColumns(width), [width]);
};

/**
 * For odd-column layouts (5, 7, 9, ...), we use a multiplied grid to allow
 * desktops to span fractional columns. The multiplier derives everything:
 *
 *   multiplier     = (columns - 1) / 2
 *   grid tracks    = columns × multiplier
 *   desktop span   = columns  (remainder tiles: columns - 1)
 *   app span       = multiplier
 *   desktops/row   = multiplier
 */
function isOddColumnLayout(columns: number): boolean {
	return columns % 2 !== 0;
}

function getMultiplier(columns: number): number {
	return (columns - 1) / 2;
}

function getGridTracks(columns: number): number {
	if (isOddColumnLayout(columns)) {
		return columns * getMultiplier(columns);
	}
	return columns;
}

function getDesktopSpan(columns: number, isRemainder: boolean): number {
	if (isOddColumnLayout(columns)) {
		return isRemainder ? columns - 1 : columns;
	}
	return 2;
}

function getAppSpan(columns: number): number {
	if (isOddColumnLayout(columns)) {
		return getMultiplier(columns);
	}
	return 1;
}

const DesktopTileWrapper = styled.div<{ columns: number; isLastDesktop: boolean }>`
	${({ columns, isLastDesktop }) => {
		const span = getDesktopSpan(columns, isLastDesktop);
		return `
			grid-column: span ${span};
			grid-row: span 2;
		`;
	}}
`;

const ResourceListGridContainer = styled.div<{
	columns: number;
}>`
	max-width: ${centerContentWidthPX}px;
	margin: 0 auto;
	display: grid;
	grid-template-columns: ${({ columns }) => `repeat(${getGridTracks(columns)}, 1fr)`};
	gap: ${layout.space16};
`;

const AppTileWrapper = styled.div<{ columns: number }>`
	${({ columns }) => {
		const span = getAppSpan(columns);
		return span > 1 ? `grid-column: span ${span};` : '';
	}}
`;

export const ResourceListDetailedView = ({ resources }: Props) => {
	const columns = useGetColumns();

	const { desktops, appsAndSharedDesktops } = useMemo(
		() =>
			resources.reduce<{ desktops: Resource[]; appsAndSharedDesktops: Resource[] }>(
				(acc, resource) => {
					if (resource.isdesktop && canQueryMachineState(resource)) {
						acc.desktops.push(resource);
					} else {
						acc.appsAndSharedDesktops.push(resource);
					}
					return acc;
				},
				{ desktops: [], appsAndSharedDesktops: [] }
			),
		[resources]
	);

	const remainderTileStartIndex = useMemo(() => {
		if (!isOddColumnLayout(columns)) {
			return -1;
		}
		// Desktops per row = multiplier: 5-col → 2, 7-col → 3, 9-col → 4
		const desktopsPerRow = getMultiplier(columns);
		const remainder = desktops.length % desktopsPerRow;
		if (remainder === 0) {
			return -1;
		}
		// All tiles from this index onward are in the incomplete last row
		return desktops.length - remainder;
	}, [desktops.length, columns]);

	return (
		<ResourceListGridContainer columns={columns} data-testid="detailed-resource-list">
			{desktops.map((resource, index) => (
				<DesktopTileWrapper
					key={resource.id}
					columns={columns}
					isLastDesktop={
						remainderTileStartIndex !== -1 && index >= remainderTileStartIndex
					}
				>
					<DetailedDesktopTileRender resource={resource} />
				</DesktopTileWrapper>
			))}

			{appsAndSharedDesktops.map(resource => (
				<AppTileWrapper key={resource.id} columns={columns}>
					<ResourceTileRenderer resource={resource} />
				</AppTileWrapper>
			))}
		</ResourceListGridContainer>
	);
};
